import { useCallback, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CandidateCard } from "../shared/card/CandidateCard";
import { CvDialog } from "../shared/dialog/CvDialog";
import { ScheduleInterviewDialog } from "../shared/candidate/CandidateInterviews";
import { EndorsementDialog } from "../shared/dialog/EndorsementDialog";
import { DeleteConfirmDialog } from "../shared/dialog/DeleteConfirmDialog";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { useFilteredList } from "@/hooks/useFilteredList";
import { matchesScoreRange } from "@/lib/candidateFilters";
import { isSuperAdmin } from "@/types";
import type { AuthUser, Candidate, Position } from "@/types";

interface Props {
  positions: Position[];
  archivedMode?: boolean;
  onViewProfile?: (id: number) => void;
  currentUser?: AuthUser;
}

type RecFilter = "all" | "strong_hire" | "consider" | "reject";
type ScoreRange = "all" | "90+" | "80-89" | "60-79" | "<60";
type StarFilter = "all" | "5" | "4" | "3" | "2" | "1";
type SortBy = "score_desc" | "score_asc" | "name_asc" | "date_desc";

const CARD_DIALOGS = ["cv", "interview", "endorse"] as const;
type CardDialog = typeof CARD_DIALOGS[number];

export function CandidatesPage({ positions, archivedMode, onViewProfile, currentUser }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const pathSegs = location.pathname.split("/").filter(Boolean);
  const baseRoute = archivedMode ? "archived" : "candidates";
  const openCandidateId = pathSegs[0] === baseRoute && pathSegs[1] && !pathSegs[2]
    ? (parseInt(pathSegs[1], 10) || null)
    : null;
  const cardDialogCandidateId = pathSegs[0] === baseRoute && pathSegs[1] && pathSegs[2]
    && (CARD_DIALOGS as readonly string[]).includes(pathSegs[2])
    ? (parseInt(pathSegs[1], 10) || null)
    : null;
  const cardDialog: CardDialog | null = cardDialogCandidateId ? (pathSegs[2] as CardDialog) : null;

  const positionLookup = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of positions) m.set(p.id, p.title);
    return m;
  }, [positions]);
  const queryClient = useQueryClient();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterPositionId, setFilterPositionId] = useState<number | null>(null);
  const [filterRec, setFilterRec] = useState<RecFilter>("all");
  const [filterScore, setFilterScore] = useState<ScoreRange>("all");
  const [filterStars, setFilterStars] = useState<StarFilter>("all");
  const [sortBy, setSortBy] = useState<SortBy>("date_desc");
  const [filterOrgId, setFilterOrgId] = useState<number | null>(null);

  const { data: orgsData } = useQuery({
    queryKey: ["organizations"],
    queryFn: () => api.listOrganizations(),
    enabled: isSuperAdmin(currentUser),
  });
  const orgs = orgsData?.organizations ?? [];

  const { data, isLoading: loading } = useQuery({
    queryKey: queryKeys.candidates({ positionId: filterPositionId ?? undefined, archived: archivedMode }),
    queryFn: () => api.listCandidates({ positionId: filterPositionId ?? undefined, archived: archivedMode }),
  });
  const candidates = data?.candidates ?? [];

  const archiveMutation = useMutation({
    mutationFn: ({ id, archived }: { id: number; archived: boolean }) => api.archiveCandidate(id, archived),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["candidates"] }),
  });
  const handleArchive = (id: number, archived: boolean) => archiveMutation.mutate({ id, archived });

  const [deleteTarget, setDeleteTarget] = useState<Candidate | null>(null);
  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.deleteCandidate(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ["candidates"] }); setDeleteTarget(null); },
  });
  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id);
  };

  const candidateFilter = useCallback(
    (c: Candidate, q: string) => {
      if (q && !(
        (c.name ?? "").toLowerCase().includes(q) ||
        (c.email ?? "").toLowerCase().includes(q) ||
        (c.filename ?? "").toLowerCase().includes(q) ||
        (c.summary ?? "").toLowerCase().includes(q) ||
        c.skills.some((s) => s.toLowerCase().includes(q))
      )) return false;
      if (filterRec !== "all" && c.recommendation !== filterRec) return false;
      if (!matchesScoreRange(c.match_score, filterScore)) return false;
      if (filterStars !== "all") {
        const minStars = Number(filterStars);
        const stars = Math.round((c.match_score / 100) * 5 * 2) / 2;
        if (!(stars >= minStars && stars < minStars + 1)) return false;
      }
      if (filterOrgId !== null && c.org_id !== filterOrgId) return false;
      return true;
    },
    [filterRec, filterScore, filterStars, filterOrgId]
  );

  const { filtered: preSort, search, setSearch } = useFilteredList(candidates, candidateFilter);

  const filtered = useMemo(
    () => [...preSort].sort((a, b) => {
      switch (sortBy) {
        case "score_desc": return b.match_score - a.match_score;
        case "score_asc": return a.match_score - b.match_score;
        case "name_asc": return (a.name ?? "").localeCompare(b.name ?? "");
        case "date_desc": return b.created_at.localeCompare(a.created_at);
      }
    }),
    [preSort, sortBy]
  );

  const counts = useMemo(
    () => ({
      all: candidates.length,
      strong_hire: candidates.filter((c) => c.recommendation === "strong_hire").length,
      consider: candidates.filter((c) => c.recommendation === "consider").length,
      reject: candidates.filter((c) => c.recommendation === "reject").length,
    }),
    [candidates]
  );

  const activeFilterCount =
    (search !== "" ? 1 : 0) +
    (filterRec !== "all" ? 1 : 0) +
    (filterScore !== "all" ? 1 : 0) +
    (filterStars !== "all" ? 1 : 0) +
    (filterPositionId !== null ? 1 : 0) +
    (filterOrgId !== null ? 1 : 0);

  const hasActiveFilters = activeFilterCount > 0;

  const clearFilters = () => {
    setSearch("");
    setFilterPositionId(null);
    setFilterRec("all");
    setFilterScore("all");
    setFilterStars("all");
    setFilterOrgId(null);
  };

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Sticky header */}
      <div className="shrink-0 shadow-sm bg-white px-3 py-4 sm:px-6">
        <div className="mx-auto max-w-6xl">
      {/* Header + search + filter toggle */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <h2 className="text-[28px] font-bold leading-[1.43] text-near-black">
            {archivedMode ? "Archived" : "All candidates"}
          </h2>
          <span className="rounded-badge bg-near-black px-2.5 py-0.5 text-xs font-semibold tabular-nums text-white">
            {candidates.length}
          </span>
        </div>
        <div className="flex flex-1 items-center gap-2 sm:max-w-md">
          <div className="relative flex-1">
            <svg
              className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-secondary"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
              />
            </svg>
            <input
              className="w-full rounded-btn border border-border-card bg-white py-2 pl-10 pr-3 text-sm text-near-black placeholder:text-text-secondary focus:outline-none focus:shadow-focus"
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            onClick={() => setFiltersOpen(!filtersOpen)}
            className={[
              "flex shrink-0 items-center gap-1.5 rounded-btn px-4 py-2 text-sm font-medium transition-colors",
              filtersOpen || hasActiveFilters
                ? "bg-near-black text-white"
                : "bg-surface-secondary text-text-focused hover:bg-near-black hover:text-white",
            ].join(" ")}
          >
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z"
              />
            </svg>
            Filters
            {hasActiveFilters && (
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-rausch text-[10px] font-bold text-white">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>
      </div>
        </div>
      </div>

      {/* Sticky filter panel */}
      {filtersOpen && (
        <div className="shrink-0 shadow-sm bg-white px-3 py-4 sm:px-6">
          <div className="mx-auto max-w-6xl">
        <div className="rounded-card bg-white p-6 shadow-card">
          {/* Filter row */}
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {isSuperAdmin(currentUser) && orgs.length > 0 && (
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-[0.32px] text-text-secondary">
                  Organization
                </label>
                <select
                  className="mt-1 w-full rounded-btn border border-border-card bg-white px-2 py-1.5 text-xs text-near-black focus:outline-none focus:shadow-focus"
                  value={filterOrgId ?? ""}
                  onChange={(e) => setFilterOrgId(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">All organizations</option>
                  {orgs.map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-[0.32px] text-text-secondary">
                Position
              </label>
              <select
                className="mt-1 w-full rounded-btn border border-border-card bg-white px-2 py-1.5 text-xs text-near-black focus:outline-none focus:shadow-focus"
                value={filterPositionId ?? ""}
                onChange={(e) =>
                  setFilterPositionId(
                    e.target.value ? Number(e.target.value) : null
                  )
                }
              >
                <option value="">All positions</option>
                {positions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-[10px] font-semibold uppercase tracking-[0.32px] text-text-secondary">
                Score range
              </label>
              <select
                className="mt-1 w-full rounded-btn border border-border-card bg-white px-2 py-1.5 text-xs text-near-black focus:outline-none focus:shadow-focus"
                value={filterScore}
                onChange={(e) => setFilterScore(e.target.value as ScoreRange)}
              >
                <option value="all">All scores</option>
                <option value="90+">90+ (Excellent)</option>
                <option value="80-89">80–89 (Strong)</option>
                <option value="60-79">60–79 (Consider)</option>
                <option value="<60">Below 60 (Reject)</option>
              </select>
            </div>

            <div>
              <label className="text-[10px] font-semibold uppercase tracking-[0.32px] text-text-secondary">
                Sort by
              </label>
              <select
                className="mt-1 w-full rounded-btn border border-border-card bg-white px-2 py-1.5 text-xs text-near-black focus:outline-none focus:shadow-focus"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortBy)}
              >
                <option value="score_desc">Score (high → low)</option>
                <option value="score_asc">Score (low → high)</option>
                <option value="name_asc">Name (A → Z)</option>
                <option value="date_desc">Newest first</option>
              </select>
            </div>

            {/* Star rating filter */}
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-[0.32px] text-text-secondary">
                Star rating
              </label>
              <div className="mt-1 flex gap-1">
                {(
                  [
                    ["all", "All"],
                    ["5", "5"],
                    ["4", "4"],
                    ["3", "3"],
                    ["2", "2"],
                    ["1", "1"],
                  ] as [StarFilter, string][]
                ).map(([value, label]) => {
                  const starColor =
                    value === "all" ? "" :
                    Number(value) >= 4 ? "text-yellow-400" :
                    Number(value) >= 2 ? "text-orange-400" : "text-[#ff0000]";
                  return (
                    <button
                      key={value}
                      onClick={() => setFilterStars(value)}
                      className={[
                        "flex items-center gap-0.5 rounded-btn px-2 py-1.5 text-xs font-medium transition-colors",
                        filterStars === value
                          ? "bg-near-black text-white"
                          : "bg-surface-secondary text-text-focused hover:bg-near-black hover:text-white",
                      ].join(" ")}
                    >
                      {value !== "all" && (
                        <svg className={`h-3 w-3 ${filterStars === value ? "text-white" : starColor}`} viewBox="0 0 20 20" fill="currentColor">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      )}
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Recommendation pills */}
          <div className="mt-3">
            <label className="text-[10px] font-semibold uppercase tracking-[0.32px] text-text-secondary">
              Recommendation
            </label>
            <div className="mt-1 flex flex-wrap gap-1">
              {(
                [
                  ["all", "All"],
                  ["strong_hire", "Strong hire"],
                  ["consider", "Consider"],
                  ["reject", "Reject"],
                ] as [RecFilter, string][]
              ).map(([value, label]) => (
                <button
                  key={value}
                  onClick={() => setFilterRec(value)}
                  className={[
                    "rounded-btn px-3 py-1.5 text-xs font-medium transition-colors",
                    filterRec === value
                      ? value === "strong_hire"
                        ? "bg-emerald-100 text-emerald-800"
                        : value === "consider"
                          ? "bg-amber-100 text-amber-800"
                          : value === "reject"
                            ? "bg-rose-100 text-error-text"
                            : "bg-near-black text-white"
                      : "bg-surface-secondary text-text-focused hover:bg-near-black hover:text-white",
                  ].join(" ")}
                >
                  {label}{" "}
                  <span className="tabular-nums opacity-70">
                    ({counts[value]})
                  </span>
                </button>
              ))}
            </div>
          </div>

          {hasActiveFilters && (
            <div className="mt-3 flex items-center justify-between">
              <p className="text-xs text-text-secondary">
                Showing {filtered.length} of {candidates.length} candidates
              </p>
              <button
                onClick={clearFilters}
                className="text-xs font-medium text-rausch hover:underline"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
          </div>
        </div>
      )}

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-6xl px-3 py-4 sm:px-6 sm:py-6">

      {/* Results */}
      {loading ? (
        <p className="text-text-secondary">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-card border-2 border-dashed border-border-card bg-white p-10 text-center text-text-secondary">
          {candidates.length === 0
            ? archivedMode
              ? "No archived candidates."
              : "No candidates have been screened yet."
            : "No candidates match the current filters."}
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((c) => (
            <CandidateCard
              key={c.id}
              candidate={c}
              positionTitle={positionLookup.get(c.position_id)}
              currentUser={currentUser}
              onArchive={handleArchive}
              onDelete={archivedMode ? (id) => {
                const target = candidates.find((x) => x.id === id);
                if (target) setDeleteTarget(target);
              } : undefined}
              onViewProfile={onViewProfile}
              initialExpanded={openCandidateId === c.id}
              onExpand={(id) => navigate(id != null ? `/${baseRoute}/${id}` : `/${baseRoute}`)}
            />
          ))}
        </div>
      )}
        </div>
      </div>

      {/* Card-level dialogs (cv/interview/endorse via URL) */}
      {(() => {
        const c = cardDialogCandidateId ? candidates.find((x) => x.id === cardDialogCandidateId) : null;
        if (!c) return null;
        const closeDialog = () => navigate(`/${baseRoute}/${cardDialogCandidateId}`);
        if (cardDialog === "cv") return <CvDialog candidateId={c.id} candidateName={c.name || ""} onClose={closeDialog} />;
        if (cardDialog === "interview") return <ScheduleInterviewDialog candidateId={c.id} onClose={closeDialog} onCreated={closeDialog} />;
        if (cardDialog === "endorse") return <EndorsementDialog candidate={c} positionTitle={c.position_title || ""} currentUser={currentUser} onClose={closeDialog} />;
        return null;
      })()}

      {/* Delete confirmation dialog */}
      {deleteTarget && (
        <DeleteConfirmDialog
          candidateName={deleteTarget.name || "this candidate"}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

